import { Icon } from "../ui/Icon";
import { useIsMobile } from "../../lib/useIsMobile";
import {
  MrpUyarilari, MrpEksikHesapNotu, MrpEldeNotu, mrpHesabiEksik, mrpEldeNotOzeti, yeterliMetni,
  MRP_ELDE_TANIMI, MRP_KAYIT_ACIGI_TANIMI, type MrpUyariVerisi, type MrpEldeUyarisi,
} from "../mrp/MrpUyarilari";
import { GENEL_MRP_DURUMLARI, GENEL_MRP_KUME, durumEtiketi, siparisDurumuMu } from "../../lib/siparisDurum";
import "./MaterialNeeds.css";

/**
 * MALZEME İHTİYACI (MRP) — sipariş kapsamlı eksik hammadde tablosu.
 *
 * ⚠ TİP SÖZLEŞMESİ: `MaterialNeedsVeri` bu bileşenin OKUDUĞU alanlardır. Çağıranlar (sipariş detayı
 * ve editörü) `order.materialNeeds` çıktısını CAST'SİZ geçer — sunucu (`MaterialNeedsSonuc`) bir alanı
 * yeniden adlandırır ya da kaldırırsa tsc çağrı yerinde yakalar. Eskiden `as unknown as` vardı ve
 * kayma sessizce `undefined` okunuyordu.
 *
 * Kullanıcı isteği (29 Ağu 2026): "sipariş aşamasında bana eksik listesi hazırlamalı,
 * MRP modülünü sipariş özelinde de çalıştırabilmeliyim."
 *
 * NEDEN PAYLAŞILAN BİLEŞEN: aynı veri hem sipariş EDİTÖRÜNDE (henüz kaydedilmemiş satırlar)
 * hem sipariş DETAYINDA (kayıtlı sipariş) gösteriliyor. İki ekran ayrı ayrı yazılsaydı biri
 * güncellenip diğeri unutulur, aynı sipariş iki yerde farklı okunurdu.
 *
 * ⚠ SESSİZLİK YASAĞI BURADA YAŞIYOR: "eksik yok" yeşil yazısı YALNIZ gerçekten hesap yapılıp
 * temiz çıktığında görünür. Reçetesizlik, atlanan satır ve yetkisizlik AYRI AYRI söylenir —
 * eskiden üçü de aynı yeşil yazıyı üretiyordu ve kullanıcı "analiz yapılamadı" diye takıldı.
 */

export interface MaterialNeedsVeri extends MrpUyariVerisi {
  satirlar: Array<{
    itemId: string; code: string; name: string; uom: string;
    /** `elde` = KULLANILABİLİR NET stok (mrp.ts `eldeHaritasi`); aşağıdaki üçü onun DIŞINDA kalanlar. */
    brut: number; elde: number; acikPo: number; eksik: number;
    /** Stokta ama kullanılamaz (elde sayılmadı): SKT geçmiş · QC/karantina/red. */
    sktGecmis: number; karantina: number;
    /** Eksi bakiyeler kullanılabilir stoğu aştı — kayıt açığı. */
    kayitAcigi: number;
    tedarikciId: string | null; tedarikciAd: string | null; tedarikSuresiGun: number | null;
  }>;
  /** MRP dışı kart türündeki (gider/hizmet) ham maddeler — eksik sayılmadı, AYRICA gösterilir. */
  mrpDisiKalemler: Array<{ itemId: string; code: string; name: string; uom: string; qty: number; tur: string }>;
  /** Eksiği OLMAYAN ama SKT geçmiş / karantina stoğu ya da kayıt açığı olan kalemler — "Eksik yok ama dikkat". */
  eldeUyarilari: MrpEldeUyarisi[];
  hesaplananKalem: number;
  yetkiYok: boolean;
  siparisDurumu: string | null;
  uretildiginde: string;
}

const fmt = (n: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);

type IhtiyacSatiri = MaterialNeedsVeri["satirlar"][number];

/**
 * TEK BİÇİMLENDİRME KAYNAĞI — masaüstü tablo ve mobil kart AYNI yardımcılardan basar.
 * Biri güncellenip öbürü unutulursa aynı eksik iki cihazda farklı okunur (ayna kuralı).
 */
const miktar = (n: number, uom: string) => `${fmt(n)} ${uom}`;

/** Tedarikçi + tedarik süresi — tabloda hücre, kartta alt satır; içerik aynı. */
function TedarikciBilgisi({ r }: { r: IhtiyacSatiri }) {
  return (
    <>
      {r.tedarikciAd ?? "—"}
      {r.tedarikSuresiGun != null ? <span className="mn-kod"> {r.tedarikSuresiGun} gün</span> : null}
    </>
  );
}

/**
 * Sipariş genel MRP'nin (Satınalma > MRP) talep kümesinde mi? Küme YEREL KOPYA DEĞİL:
 * `lib/siparisDurum` `GENEL_MRP_DURUMLARI` = sunucu `mrp.ts` `MRP_TALEP_DURUMLARI` aynası (parite
 * `test-order-status-type-filter.ts` bölüm L). Eskiden burada ayrı bir dizi vardı — sunucu kümesi
 * değişseydi bu kart taslak uyarısını yanlış siparişlere basardı. Tanınmayan durum kodu kümede
 * SAYILMAZ (uyarı basılır, kod adıyla görünür).
 */
const genelMrpTalebinde = (durum: string) => siparisDurumuMu(durum) && GENEL_MRP_DURUMLARI.includes(durum);

export function MaterialNeeds({
  veri, yukleniyor, hata, yenile, yenileniyor,
}: {
  veri?: MaterialNeedsVeri;
  yukleniyor: boolean;
  hata?: string;
  yenile?: () => void;
  yenileniyor?: boolean;
}) {
  // ≤767px: 6 sütunlu tablo 345px'e sığmıyor, iki yönde kayan kutu oluyordu → KART listesi.
  const isMobile = useIsMobile();

  const govde = () => {
    if (hata) {
      return <div className="mn-msg err" role="alert"><Icon name="alertTriangle" size={14} />{hata}</div>;
    }
    if (yukleniyor || !veri) {
      return <div className="mn-msg">Malzeme ihtiyacı hesaplanıyor…</div>;
    }
    if (veri.yetkiYok) {
      // Reçete kırılımı mahrem — boş liste GÖSTERMEK yanıltıcı olurdu ("eksik yok" sanılır).
      return (
        <div className="mn-msg info">
          <Icon name="lock" size={14} />
          Bu analiz reçete kırılımı içerir — görmek için “Reçete kırılımını görüntüle” (view:recipe) izni gerekir.
        </div>
      );
    }

    /**
     * Motorun hesaba KATAMADIKLARI paylaşılan bileşenden basılır (`mrp/MrpUyarilari`) —
     * genel MRP ekranı (Satınalma > MRP) de AYNI bileşeni kullanır. Metin burada kopyalansaydı
     * iki ekran aynı motorun aynı susmasını farklı anlatırdı (ayna kuralı).
     * MRP dışı (gider/hizmet) kalemler de aynı bileşenden: eksik sayılmadılar ama SÖYLENİRLER.
     */
    const uyarilar = (
      <MrpUyarilari veri={veri} mrpDisiKalemler={veri.mrpDisiKalemler} eldeUyarilari={veri.eldeUyarilari} />
    );
    const hesapEksik = mrpHesabiEksik(veri);
    /**
     * ELDE TANIMI DİPNOTU — "Eldeki" artık kullanılabilir NET stok. Bir satırda (ya da "eksik yok ama
     * dikkat" kutusundaki bir kalemde) bile elde dışı stok/kayıt açığı notu varsa tanım yazılır; yoksa
     * kullanıcı Stok ekranındaki miktarla karşılaştırıp hesabın yanlış olduğunu sanır.
     * Ne zaman basılacağı genel MRP ekranıyla AYNI yardımcıdan (`mrpEldeNotOzeti`).
     */
    const eldeOzeti = mrpEldeNotOzeti([...veri.satirlar, ...veri.eldeUyarilari]);
    // Görünürlük kuralı genel MRP ekranıyla AYNI: eksik satırı varsa (Eldeki sütunu görünür) ya da not varsa (V9).
    const eldeDipnotu = veri.satirlar.length > 0 || eldeOzeti.notVar ? (
      <p className="mn-dipnot">
        <b>Eldeki</b> = {MRP_ELDE_TANIMI}.
        {eldeOzeti.acikVar ? <> <b>Kayıt açığı</b> = {MRP_KAYIT_ACIGI_TANIMI}.</> : null}
      </p>
    ) : null;

    // Hiçbir kalem hesaplanamadıysa YEŞİL YAZI GÖSTERİLMEZ — hesap yapılmadı demektir.
    if (veri.hesaplananKalem === 0) {
      return <>{uyarilar}{!hesapEksik ? <div className="mn-msg">Hesaplanacak üretim kalemi yok.</div> : null}</>;
    }

    return (
      <>
        {uyarilar}
        {veri.satirlar.length === 0 ? (
          // "Yeterli" ancak hesap TAM ise söylenir: motorun atladığı TEK bir kalem bile varsa
          // (reçetesiz kök, patlatmaya girmeyen satır ya da patlatılamayan dal) liste eksiktir.
          hesapEksik ? (
            <MrpEksikHesapNotu metin="Hesaba GİREN kalemlerde eksik çıkmadı — ancak yukarıdaki kalemler hesaba katılamadığı için bu sonuç TAM DEĞİL; yeterlilik iddiasında bulunulamaz." />
          ) : (
            /*
             * Yeterlilik cümlesi TEK KAYNAKTAN (`yeterliMetni`) — genel MRP ekranı ve A4 rapor da aynısını
             * basar. MRP dışı kalem ya da elde uyarısı varsa iddia "MRP'ye dahil hammaddelerde eksik yok"
             * diye DARALIR. Tek <span>: `.mn-msg` flex, metin parçaları ayrı sütunlara bölünmesin.
             */
            <div className="mn-msg ok">
              <Icon name="checkCircle" size={14} />
              <span>
                {yeterliMetni({ mrpDisiKalemler: veri.mrpDisiKalemler, eldeUyarilari: veri.eldeUyarilari })}{" "}
                {veri.hesaplananKalem} ürün kalemi hesaplandı.
              </span>
            </div>
          )
        ) : isMobile ? (
          /*
           * MOBİL: TABLO → KART (proje deseni, bkz. mrp/OrderMrpSelector). Kaydırma kutusu YOK —
           * sayfanın içinde ikinci dikey kaydırıcı dokunmatikte tuzak oluyordu. Eksik, kartın
           * EN BELİRGİN değeri ve "Eksik" etiketiyle yazılır — renk tek başına anlam taşımaz.
           */
          <ul className="mn-kartlar" aria-label="Eksik hammadde listesi">
            {veri.satirlar.map((r) => (
              <li key={r.itemId} className="mn-kart">
                <div className="mn-kart-ust">
                  <span className="mn-kart-ad">{r.name}</span>
                  <span className="mn-kart-kod">{r.code}</span>
                </div>
                <div className="mn-kart-eksik">
                  <span className="lbl"><Icon name="alertTriangle" size={13} />Eksik</span>
                  <span className="val mn-eksik">{miktar(r.eksik, r.uom)}</span>
                </div>
                <dl className="mn-kart-kv">
                  <div><dt>Brüt</dt><dd>{miktar(r.brut, r.uom)}</dd></div>
                  <div><dt>Eldeki</dt><dd>{miktar(r.elde, r.uom)}</dd></div>
                  <div><dt>Açık PO</dt><dd>{miktar(r.acikPo, r.uom)}</dd></div>
                </dl>
                {/* Elde sayılmayan stok — 3'lü ızgaranın dar hücresine sığmaz, ızgaranın altında tam genişlik. */}
                <MrpEldeNotu sktGecmis={r.sktGecmis} karantina={r.karantina} kayitAcigi={r.kayitAcigi} uom={r.uom} yerlesim="satir" />
                {r.tedarikciAd != null || r.tedarikSuresiGun != null ? (
                  <div className="mn-kart-ted">
                    <span className="lbl">Tedarikçi</span> <TedarikciBilgisi r={r} />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="mn-scroll">
            <table className="mn-tbl">
              <thead>
                <tr>
                  <th>Hammadde</th>
                  <th className="r">Brüt İhtiyaç</th>
                  <th className="r">Eldeki</th>
                  <th className="r">Açık PO</th>
                  <th className="r">Eksik</th>
                  <th>Tedarikçi</th>
                </tr>
              </thead>
              <tbody>
                {veri.satirlar.map((r) => (
                  <tr key={r.itemId}>
                    <td>
                      {r.name} <span className="mn-kod mono">{r.code}</span>
                    </td>
                    <td className="r mono">{miktar(r.brut, r.uom)}</td>
                    <td className="r mono mn-soluk">
                      {miktar(r.elde, r.uom)}
                      <MrpEldeNotu sktGecmis={r.sktGecmis} karantina={r.karantina} kayitAcigi={r.kayitAcigi} uom={r.uom} />
                    </td>
                    <td className="r mono mn-soluk">{miktar(r.acikPo, r.uom)}</td>
                    <td className="r mono mn-eksik">{miktar(r.eksik, r.uom)}</td>
                    <td className="mn-soluk">
                      <TedarikciBilgisi r={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {eldeDipnotu}
      </>
    );
  };

  /**
   * KAPSAM DIŞI UYARISI: genel MRP (Satınalma > MRP) talebi yalnız `GENEL_MRP_KUME` (Onaylı ·
   * Üretimde · Hazır) durumundaki siparişlerden toplar (mrp.ts `MRP_TALEP_DURUMLARI`). Taslak,
   * sevk edilmiş ya da iptal sipariş orada HİÇ görünmez — kullanıcı "MRP bu siparişi görmüyor" diye
   * takılmasın diye burada açıkça yazılır. Küme ve durum adı tek kaynaktan (`lib/siparisDurum`);
   * eskiden küme elle yazılmıştı ve taslak dışındaki durumlar ham kodla ("shipped") basılıyordu.
   * ⚠ "Açık sipariş" DENMEZ: Siparişler ekranındaki "Açık siparişler" kısayolu başka bir kümedir.
   */
  const taslakUyarisi =
    veri && veri.siparisDurumu && !genelMrpTalebinde(veri.siparisDurumu) ? (
      <div className="mn-msg info">
        <Icon name="info" size={14} />
        {/* TEK <span>: `.mn-msg` flex; `<b>`ler ayrı flex öğesi olup metni dar sütunlara bölüyordu
            (MrpUyarilari deseni). */}
        <span>
          Bu sipariş <b>{durumEtiketi(veri.siparisDurumu)}</b> durumunda — genel MRP
          ekranı yalnız <b>{GENEL_MRP_KUME}</b> durumundaki siparişleri talep sayar. Buradaki hesap bu siparişe özeldir.
        </span>
      </div>
    ) : null;

  return (
    <div className="d-card mn-card">
      <div className="d-card-head">
        <div className="left">
          <span className="t"><Icon name="layers" size={12} /> Malzeme İhtiyacı (MRP)</span>
          <span className="s">bu sipariş için gereken hammadde</span>
        </div>
        {yenile ? (
          <button type="button" className="btn-md secondary sm mn-hesapla" onClick={yenile} disabled={yenileniyor}>
            <Icon name="refresh" size={13} />{yenileniyor ? "Hesaplanıyor…" : "Yeniden hesapla"}
          </button>
        ) : yukleniyor ? (
          <span className="mn-bas-durum" role="status">hesaplanıyor…</span>
        ) : null}
      </div>
      <div className="d-card-body flush">
        {taslakUyarisi}
        {govde()}
      </div>
    </div>
  );
}

/**
 * HESAP KAPISI — MRP koşusu İSTEK ÜZERİNE başlar.
 *
 * Hesap reçete patlatması + her hammadde için stok ve açık PO sorgusu demek; ekran
 * açıldığında kendiliğinden koşmaz. Sipariş EDİTÖRÜ ile sipariş DETAYI AYNI kapıyı
 * paylaşır — aynı ağır hesap bir ekranda kapılı, öbüründe kapısız olamaz. (Editörde
 * kapı yokken, mevcut bir sipariş düzenlemek için çekmece açıldığında satırlar
 * kendiliğinden dolduğu için kullanıcı hiçbir şeye dokunmadan tam MRP koşuyordu.)
 *
 * ⚠ SESSİZLİK YASAĞI: henüz koşmamış hesabın yerine BOŞ TABLO gösterilmez — "eksik yok"
 * diye okunurdu. Ne hesaplanacağı yazılır, düğme gösterilir.
 */
export function MaterialNeedsKapi({ onCalistir }: { onCalistir: () => void }) {
  return (
    <div className="d-card mn-card">
      <div className="d-card-head">
        <div className="left">
          <span className="t"><Icon name="layers" size={12} /> Malzeme İhtiyacı (MRP)</span>
          <span className="s">bu sipariş için gereken hammadde</span>
        </div>
        <button type="button" className="btn-md secondary sm mn-hesapla" onClick={onCalistir}>
          <Icon name="play" size={13} />Hesapla
        </button>
      </div>
      <div className="d-card-body flush">
        <div className="mn-msg">
          Sipariş satırlarının reçetesi patlatılıp hammadde eksiği hesaplanır — MRP motorunun
          aynısı, yalnız bu siparişe kapsanmış olarak.
        </div>
      </div>
    </div>
  );
}
